import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Upload, FileCheck, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import FormConsent from "@/components/FormConsent";

const AVAILABILITIES = ["Weekday days", "Weekday evenings", "Weekday overnight", "Weekends", "Flexible / any"];
const SHIFTS = ["Part-time", "Full-time", "Live-in", "Per-diem / as needed"];
const EXPERIENCE = ["New to care", "Less than 1 year", "1–3 years", "3–5 years", "5+ years"];

function FilePicker({ label, fileId, onUpload, folder, testId }) {
  const [uploading, setUploading] = useState(false);
  const [filename, setFilename] = useState("");

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", folder);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(data.id);
      setFilename(data.filename);
      toast.success(`${data.filename} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <label className={`flex items-center gap-4 rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer transition ${fileId ? "border-teal-400 bg-teal-50/50" : "border-slate-200 hover:border-teal-300 bg-white"}`} data-testid={testId}>
      <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
        {fileId ? <FileCheck className="w-5 h-5 text-teal-600" /> : <Upload className="w-5 h-5 text-slate-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500 truncate">
          {uploading ? "Uploading…" : fileId ? filename || "File attached" : "PDF, DOC, DOCX, JPG or PNG (up to 10MB)"}
        </div>
      </div>
      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" onChange={handle} />
    </label>
  );
}

export default function Careers() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    availability: "", shift_preference: "", has_transportation: false,
    years_experience: "", experience_summary: "", certifications: "",
    background_check_consent: false,
    terms_ok: false,
    resume_file_id: null, certification_file_id: null,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const chk = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));
  const setFile = (k) => (id) => setForm((f) => ({ ...f, [k]: id }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.background_check_consent) {
      toast.error("Please confirm background check consent to apply.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/caregiver-applications", form);
      toast.success("Application submitted!");
      setSubmitted(true);
      track("caregiver_application_submitted", {
        experience: form.years_experience,
        shift: form.shift_preference,
        has_resume: !!form.resume_file_id,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="section" data-testid="careers-success">
        <div className="container-x max-w-2xl soft-card text-center">
          <CheckCircle2 className="w-14 h-14 text-teal-600 mx-auto" />
          <h1 className="mt-6 text-3xl font-semibold text-slate-900">Application received.</h1>
          <p className="mt-4 text-slate-600">Thanks for your interest in joining Melo Hopeful Care. Our hiring team reviews every application personally and will reach out soon.</p>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="careers-page">
      <section className="section bg-gradient-to-b from-slate-50 to-white">
        <div className="container-x max-w-4xl">
          <div className="eyebrow">Careers</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Become the reason a family <span className="brand-gradient-text">feels supported.</span>
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-2xl">
            We hire kind, reliable caregivers — and we invest in them. Flexible schedules, paid training, growth paths, and a team that genuinely has your back.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x max-w-4xl">
          <form onSubmit={submit} className="soft-card space-y-7" data-testid="careers-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="label-field">Full name *</label>
                <input required value={form.full_name} onChange={upd("full_name")} className="input-field" data-testid="cr-name" />
              </div>
              <div>
                <label className="label-field">Email *</label>
                <input required type="email" value={form.email} onChange={upd("email")} className="input-field" data-testid="cr-email" />
              </div>
              <div>
                <label className="label-field">Phone *</label>
                <input required type="tel" value={form.phone} onChange={upd("phone")} className="input-field" data-testid="cr-phone" />
              </div>
              <div>
                <label className="label-field">Years of experience *</label>
                <select required value={form.years_experience} onChange={upd("years_experience")} className="input-field" data-testid="cr-experience">
                  <option value="">Select experience</option>
                  {EXPERIENCE.map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="label-field">Availability *</label>
                <select required value={form.availability} onChange={upd("availability")} className="input-field" data-testid="cr-availability">
                  <option value="">Select availability</option>
                  {AVAILABILITIES.map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="label-field">Shift preference *</label>
                <select required value={form.shift_preference} onChange={upd("shift_preference")} className="input-field" data-testid="cr-shift">
                  <option value="">Select shift</option>
                  {SHIFTS.map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Experience summary</label>
                <textarea rows={4} value={form.experience_summary} onChange={upd("experience_summary")} className="input-field" data-testid="cr-experience-summary" placeholder="Briefly describe past caregiving experience, settings and specialties." />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Certifications (CNA, HHA, CPR, etc.)</label>
                <input value={form.certifications} onChange={upd("certifications")} className="input-field" data-testid="cr-certifications" placeholder="e.g. CNA, CPR, First Aid" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <FilePicker label="Resume (optional)" fileId={form.resume_file_id} onUpload={setFile("resume_file_id")} folder="resumes" testId="cr-resume-upload" />
              <FilePicker label="Certification / credentials (optional)" fileId={form.certification_file_id} onUpload={setFile("certification_file_id")} folder="certifications" testId="cr-cert-upload" />
            </div>

            <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
              <input id="transport" type="checkbox" checked={form.has_transportation} onChange={chk("has_transportation")} className="mt-1 h-4 w-4 rounded accent-teal-600" data-testid="cr-transportation" />
              <label htmlFor="transport" className="text-sm text-slate-700">I have reliable transportation to reach clients.</label>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-teal-100 bg-teal-50/40">
              <input id="bgcheck" type="checkbox" required checked={form.background_check_consent} onChange={chk("background_check_consent")} className="mt-1 h-4 w-4 rounded accent-teal-600" data-testid="cr-bgcheck" />
              <label htmlFor="bgcheck" className="text-sm text-slate-700">I consent to a background check as part of the hiring process. *</label>
            </div>

            <FormConsent type="careers" checked={form.terms_ok} onChange={chk("terms_ok")} testId="cr-consent" />

            <div className="flex items-center gap-4">
              <button disabled={loading} className="btn-primary" data-testid="cr-submit">
                <UserPlus className="w-4 h-4" /> {loading ? "Submitting…" : "Submit application"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
